<template>
  <v-dialog v-model="dialog" max-width="800px" class="highestZIndex">
    <v-card class="pa-5">
      <v-tabs v-model="uploadMode" class="mb-4">
        <!-- Single Upload tab commented out for now -->
        <!-- <v-tab>Single Upload</v-tab> -->
        <v-tab>Add Audio Track(s)</v-tab>
        <v-tab>Add Video(s)</v-tab>
        <v-tab>Polls</v-tab>
      </v-tabs>

      <v-tabs-items v-model="uploadMode">
        <!-- Single Upload Tab - COMMENTED OUT
        <v-tab-item>
          <v-form ref="form" v-model="uploadRecordingFormValid">
            <v-file-input
              accept=".wav"
              label="wav"
              type="file"
              @change="handleHighQualityFileUpload($event)"
            ></v-file-input>
            <v-file-input
              accept=".mp3"
              label="mp3"
              type="file"
              @change="handleRegularQualityFileUpload($event)"
            ></v-file-input>

            <v-text-field
              v-model="title"
              :rules="titleRules"
              label="Title"
              required
              prepend-icon="mdi-file-document-edit-outline"
            ></v-text-field>

            <v-dialog
              ref="dateDialog"
              v-model="datePickerModal"
              :return-value.sync="uploadRecordingDate"
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="uploadRecordingDate"
                  label="Recording Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  @mousedown.prevent
                  :rules="[(v) => !!v || 'Required']"
                ></v-text-field>
              </template>
              <v-date-picker v-model="uploadRecordingDate" scrollable>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="datePickerModal = false">
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.dateDialog.save(uploadRecordingDate)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-dialog>

            <v-btn
              block
              :disabled="
                !uploadRecordingFormValid ||
                !uploadRecordingDate ||
                !highQualityFile ||
                !regularQualityFile
              "
              :loading="isUploading"
              color="success"
              @click="submitFiles"
              class="mt-2"
            >
              Upload
            </v-btn>
          </v-form>
        </v-tab-item>
        -->

        <!-- Add Audio Tracks Tab (formerly Batch Upload) -->
        <v-tab-item>
          <v-file-input
            accept=".wav,.mp3"
            label="Select Files (wav and mp3)"
            multiple
            @change="handleBatchFileSelection"
            class="mb-4"
          ></v-file-input>

          <v-alert v-if="batchUploadError" type="error" class="mb-4">
            {{ batchUploadError }}
          </v-alert>

          <v-simple-table v-if="batchTracks.length > 0" class="mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 40px"></th>
                  <th class="text-left">Track Name</th>
                  <th class="text-left">Year</th>
                  <th class="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(track, index) in batchTracks" :key="index">
                  <td style="width: 40px">
                    <v-btn
                      v-if="track.status !== 'completed'"
                      icon
                      small
                      @click="removeTrack(index)"
                      :disabled="track.status === 'uploading'"
                    >
                      <v-icon small color="error">mdi-close</v-icon>
                    </v-btn>
                  </td>
                  <td>
                    <v-text-field
                      v-model="track.name"
                      :disabled="track.status !== 'pending'"
                      dense
                      hide-details
                      solo
                      flat
                      :rules="[(v) => v && v.length <= 250 || 'Max 250 characters']"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-select
                      v-model="track.year"
                      :items="yearOptions"
                      dense
                      :disabled="track.status !== 'pending'"
                      style="max-width: 100px"
                      hide-details
                      solo
                      flat
                    ></v-select>
                  </td>
                  <td style="min-width: 160px">
                    <v-icon v-if="track.status === 'completed'" color="success">
                      mdi-check-circle
                    </v-icon>
                    <div v-else-if="track.status === 'uploading'" style="min-width: 140px">
                      <v-progress-linear
                        :value="track.progress || 0"
                        color="primary"
                        height="8"
                        rounded
                      ></v-progress-linear>
                      <div class="caption text--secondary mt-1">{{ track.statusText || 'Uploading...' }}</div>
                    </div>
                    <v-tooltip v-else-if="track.status === 'error'" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" v-bind="attrs" v-on="on">
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      <span>{{ track.errorMessage }}</span>
                    </v-tooltip>
                    <v-tooltip v-else-if="track.validationError" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="warning" v-bind="attrs" v-on="on">
                          mdi-alert
                        </v-icon>
                      </template>
                      <span>{{ track.validationError }}</span>
                    </v-tooltip>
                    <v-tooltip v-else bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="primary" v-bind="attrs" v-on="on">
                          mdi-check-circle
                        </v-icon>
                      </template>
                      <span>Ready to upload</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-btn
            block
            :disabled="!batchUploadValid"
            :loading="isBatchUploading"
            color="success"
            @click="submitBatchUpload"
            class="mt-2"
          >
            Upload All
          </v-btn>
        </v-tab-item>

        <!-- Add Videos Tab -->
        <v-tab-item>
          <div class="mb-4">
            <div
              v-for="(video, index) in videoEntries"
              :key="index"
              class="video-entry mb-4 pa-3"
              :class="{ 'video-entry-border': videoEntries.length > 1 }"
            >
              <v-row align="center">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="video.url"
                    label="YouTube URL"
                    placeholder="https://youtube.com/watch?v=..."
                    prepend-icon="mdi-youtube"
                    :rules="[validateYouTubeUrl]"
                    :disabled="video.status !== 'pending'"
                    dense
                    @input="extractVideoId(video)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="video.name"
                    label="Video Name"
                    prepend-icon="mdi-pencil"
                    :rules="[(v) => !!v || 'Name is required', (v) => v && v.length <= 250 || 'Max 250 characters']"
                    :disabled="video.status !== 'pending'"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="12" sm="6">
                  <v-dialog
                    :ref="'videoDateDialog' + index"
                    v-model="video.datePickerOpen"
                    width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="video.date"
                        label="Recording Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :rules="[(v) => !!v || 'Date is required']"
                        :disabled="video.status !== 'pending'"
                        dense
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="video.date" scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="video.datePickerOpen = false">
                        Cancel
                      </v-btn>
                      <v-btn text color="primary" @click="video.datePickerOpen = false">
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col cols="10" sm="4" class="youtube-id-chip-col">
                  <v-chip v-if="video.youtubeId && !isDuplicateVideo(video.youtubeId)" small color="success" outlined class="youtube-id-chip">
                    ID: {{ video.youtubeId }}
                  </v-chip>
                  <v-tooltip v-else-if="video.youtubeId && isDuplicateVideo(video.youtubeId)" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip small color="warning" outlined class="youtube-id-chip" v-bind="attrs" v-on="on">
                        <v-icon small left>mdi-alert</v-icon>
                        Duplicate
                      </v-chip>
                    </template>
                    <span>This video has already been added</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="2" class="text-right">
                  <v-btn
                    v-if="video.status === 'pending' && videoEntries.length > 1"
                    icon
                    small
                    @click="removeVideoEntry(index)"
                    color="error"
                  >
                    <v-icon small>mdi-minus-circle</v-icon>
                  </v-btn>
                  <v-icon v-else-if="video.status === 'completed'" color="success">
                    mdi-check-circle
                  </v-icon>
                  <v-progress-circular
                    v-else-if="video.status === 'uploading'"
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  ></v-progress-circular>
                  <v-icon v-else-if="video.status === 'error'" color="error">
                    mdi-alert-circle
                  </v-icon>
                </v-col>
              </v-row>
            </div>

            <!-- Add another video button -->
            <v-btn
              text
              color="primary"
              @click="addVideoEntry"
              :disabled="isAddingVideos"
              class="mb-4"
            >
              <v-icon left>mdi-plus</v-icon>
              Add another video
            </v-btn>
          </div>

          <v-alert v-if="videoUploadError" type="error" class="mb-4">
            {{ videoUploadError }}
          </v-alert>

          <v-btn
            block
            :disabled="!videosValid"
            :loading="isAddingVideos"
            color="success"
            @click="submitVideos"
          >
            Add Video(s)
          </v-btn>
        </v-tab-item>

        <!-- Polls Tab (super admin) -->
        <v-tab-item class="pt-4">
          <!-- CREATE POLL -->
          <v-text-field
            v-model="pollTitle"
            label="Poll title"
            prepend-icon="mdi-poll"
            class="mt-2"
            :rules="[(v) => !!v || 'Title is required', (v) => !v || v.length <= 200 || 'Max 200 characters']"
            dense
          ></v-text-field>

          <div class="caption text--secondary mb-1">
            Options (categories) — up to 3, applied to every track
          </div>
          <div
            v-for="(opt, index) in pollOptions"
            :key="'pollopt-' + index"
            class="d-flex align-center"
          >
            <v-text-field
              v-model="opt.label"
              :label="'Option ' + (index + 1)"
              dense
              class="mr-2"
            ></v-text-field>
            <v-btn
              v-if="pollOptions.length > 1"
              icon
              small
              color="error"
              @click="removePollOption(index)"
            >
              <v-icon small>mdi-minus-circle</v-icon>
            </v-btn>
          </div>
          <v-btn
            v-if="pollOptions.length < 3"
            text
            color="primary"
            class="mb-2"
            @click="addPollOption"
          >
            <v-icon left>mdi-plus</v-icon> Add option
          </v-btn>

          <v-alert v-if="pollCreateError" type="error" dense class="mb-2">
            {{ pollCreateError }}
          </v-alert>

          <v-btn
            block
            color="success"
            class="mt-2 mb-6"
            :disabled="!pollFormValid"
            :loading="isCreatingPoll"
            @click="createPollSubmit"
          >
            Create Poll
          </v-btn>

          <!-- EXISTING POLLS -->
          <v-divider class="mb-3"></v-divider>
          <div class="subtitle-2 mb-2">Existing polls</div>
          <div v-if="adminPolls.length === 0" class="caption text--secondary">
            No polls yet.
          </div>
          <v-card
            v-for="poll in adminPolls"
            :key="poll.id"
            outlined
            class="mb-2 pa-3"
          >
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div class="mr-2">
                <span class="font-weight-medium">{{ poll.title }}</span>
                <v-chip x-small label :color="statusColor(poll.status)" class="ml-2">
                  {{ poll.status }}
                </v-chip>
                <div class="caption text--secondary">
                  {{ poll.voter_count }} voter{{ poll.voter_count === 1 ? '' : 's' }}
                  · {{ (poll.options || []).map(o => o.label).join(', ') }}
                </div>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  v-if="poll.status !== 'active'"
                  x-small
                  text
                  color="primary"
                  :loading="busyPollId === poll.id"
                  @click="activatePoll(poll)"
                >
                  Activate
                </v-btn>
                <v-btn
                  v-else
                  x-small
                  text
                  :loading="busyPollId === poll.id"
                  @click="closePoll(poll)"
                >
                  Close
                </v-btn>
                <v-btn
                  x-small
                  text
                  color="primary"
                  :loading="linkBusyPollId === poll.id"
                  @click="generateResultsLink(poll)"
                >
                  {{ poll.share_hash ? 'Copy link' : 'Results link' }}
                </v-btn>
                <v-btn
                  x-small
                  icon
                  color="error"
                  @click="askDeletePoll(poll)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-if="poll.share_hash" class="mt-2">
              <div
                v-for="link in resultsLinks(poll)"
                :key="link.label"
                class="d-flex align-center mb-1"
              >
                <v-chip x-small label class="mr-2" style="min-width: 62px; justify-content: center;">
                  {{ link.label }}
                </v-chip>
                <v-text-field
                  :value="link.url"
                  readonly
                  dense
                  hide-details
                  class="poll-link-field"
                  @focus="$event.target.select()"
                ></v-text-field>
                <v-btn x-small icon class="ml-1" title="Copy link" @click="copyLink(link.url)">
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
                <v-btn x-small icon class="ml-1" title="Open in new tab" @click="openLink(link.url)">
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- DELETE POLL CONFIRMATION -->
          <v-dialog v-model="isShowingPollDeleteDialog" max-width="400">
            <v-card>
              <v-card-title class="text-h6">
                Delete '{{ pendingDeletePoll ? pendingDeletePoll.title : '' }}'?
              </v-card-title>
              <v-card-text>
                This removes the poll and its votes from the lists. It can't be undone from here.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="cancelDeletePoll">Cancel</v-btn>
                <v-btn
                  color="error"
                  text
                  :loading="deleteBusyPollId !== null"
                  @click="confirmDeletePoll"
                >
                  Delete
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VideoApiService from '@/services/VideoApiService';

export default {
  name: 'UploadDialog',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // Single upload properties (kept for potential future use)
      title: '',
      titleRules: [
        (v) => !!v || "Title is required",
        (v) => (v && v.length <= 250) || "Title must be less than 250 characters",
      ],
      highQualityFile: null,
      regularQualityFile: null,
      uploadRecordingFormValid: false,
      uploadRecordingDate: null,
      datePickerModal: false,

      // Batch upload properties
      uploadMode: 0,
      batchTracks: [],
      batchUploadError: "",
      isBatchUploading: false,

      // Video upload properties
      videoEntries: [this.createEmptyVideoEntry()],
      videoUploadError: "",
      isAddingVideos: false,

      // Poll properties
      pollTitle: '',
      pollOptions: [{ label: '' }, { label: '' }],
      isCreatingPoll: false,
      pollCreateError: '',
      busyPollId: null,
      linkBusyPollId: null,
      deleteBusyPollId: null,
      pendingDeletePoll: null,
      isShowingPollDeleteDialog: false,
    };
  },

  computed: {
    ...mapGetters({
      isUploading: 'upload/isUploading',
      uploadError: 'upload/uploadError',
      existingRecordings: 'playlist/recordings',
      existingVideos: 'video/videos',
      adminPolls: 'poll/adminPolls',
    }),

    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear; year >= 2000; year--) {
        years.push(year);
      }
      return years;
    },

    batchUploadValid() {
      return this.batchTracks.length > 0 &&
             this.batchTracks.every(track =>
               track.year &&
               track.year !== '' &&
               !track.validationError
             );
    },

    videosValid() {
      return this.videoEntries.length > 0 &&
             this.videoEntries.every(video =>
               video.youtubeId &&
               video.name &&
               video.name.trim() !== '' &&
               video.date &&
               video.status === 'pending' &&
               !this.isDuplicateVideo(video.youtubeId)
             );
    },

    pollFormValid() {
      const labels = this.pollOptions.map(o => (o.label || '').trim()).filter(Boolean);
      return (
        this.pollTitle.trim() !== '' &&
        labels.length === this.pollOptions.length &&
        labels.length >= 1 &&
        labels.length <= 3
      );
    },
  },

  watch: {
    isUploading(newValue) {
      if (newValue) return;
      // Upload finished. Only a clean finish is a success — on failure keep the form
      // (and the chosen files) so the user can fix the name and retry.
      if (this.uploadError) {
        this.showSnackbar(this.uploadError);
        return;
      }
      this.showUploadSuccessMessage();
      this.resetForm();
      this.dialog = false;
    },

    title(newValue) {
      if (newValue.includes("/") || newValue.includes("\\")) {
        this.title = newValue.replace("/", " ").replace("\\", " ");
      }
    },

    batchTracks: {
      deep: true,
      handler(tracks) {
        // Re-validate tracks when names change
        tracks.forEach(track => {
          if (track.status === 'pending') {
            track.validationError = this.validateTrack(track);
          }
        });
      }
    },

    dialog(newValue) {
      // Reset video entries when dialog opens
      if (newValue) {
        this.videoEntries = [this.createEmptyVideoEntry()];
        this.videoUploadError = "";
        // If opening straight onto the Polls tab, load polls
        if (this.uploadMode === 2) {
          this.$store.dispatch('poll/fetchPolls').catch(() => {});
        }
      }
    },

    uploadMode(newValue) {
      // Load polls when the Polls tab (index 2) is opened
      if (newValue === 2) {
        this.$store.dispatch('poll/fetchPolls').catch(() => {});
      }
    },
  },

  methods: {
    ...mapActions({
      setHighQualityFile: 'upload/setHighQualityFile',
      setRegularQualityFile: 'upload/setRegularQualityFile',
      uploadFiles: 'upload/uploadFiles',
      uploadBatchFile: 'upload/uploadBatchFile',
      showSnackbar: 'ui/showSnackbar',
      loadVideos: 'video/loadVideos',
      loadMasterPlaylist: 'playlist/loadMasterPlaylist',
    }),

    createEmptyVideoEntry() {
      return {
        url: '',
        youtubeId: null,
        name: '',
        date: null,
        datePickerOpen: false,
        status: 'pending',
        errorMessage: null,
      };
    },

    addVideoEntry() {
      this.videoEntries.push(this.createEmptyVideoEntry());
    },

    removeVideoEntry(index) {
      if (this.videoEntries.length > 1) {
        this.videoEntries.splice(index, 1);
      }
    },

    extractVideoId(video) {
      const url = video.url;
      if (!url) {
        video.youtubeId = null;
        return;
      }

      // Match various YouTube URL formats
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
        /^([a-zA-Z0-9_-]{11})$/ // Just the video ID
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          video.youtubeId = match[1];
          return;
        }
      }

      video.youtubeId = null;
    },

    validateYouTubeUrl(value) {
      if (!value) return 'YouTube URL is required';

      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
        /^([a-zA-Z0-9_-]{11})$/
      ];

      for (const pattern of patterns) {
        if (pattern.test(value)) {
          return true;
        }
      }

      return 'Please enter a valid YouTube URL';
    },

    async submitVideos() {
      this.isAddingVideos = true;
      this.videoUploadError = "";

      const videosToAdd = this.videoEntries
        .filter(v => v.status === 'pending' && v.youtubeId && v.name && v.date)
        .map(v => ({
          name: v.name.trim(),
          youtubeId: v.youtubeId,
          date: v.date,
          year: new Date(v.date).getFullYear()
        }));

      if (videosToAdd.length === 0) {
        this.videoUploadError = "No valid videos to add";
        this.isAddingVideos = false;
        return;
      }

      try {
        // Mark all as uploading
        this.videoEntries.forEach(v => {
          if (v.status === 'pending' && v.youtubeId && v.name && v.date) {
            v.status = 'uploading';
          }
        });

        const response = await VideoApiService.addVideos(videosToAdd);

        if (response.status === 'success' || response.status === 'partial') {
          // Mark successful ones as completed
          if (response.results) {
            response.results.forEach(result => {
              const entry = this.videoEntries.find(v => v.name.trim() === result.name);
              if (entry) {
                entry.status = 'completed';
              }
            });
          }

          // Mark failed ones as error
          if (response.errors) {
            response.errors.forEach(error => {
              const entry = this.videoEntries.find(v => v.name.trim() === error.video);
              if (entry) {
                entry.status = 'error';
                entry.errorMessage = error.error;
              }
            });
          }

          // Reload videos in the store
          await this.loadVideos();

          const successCount = response.results ? response.results.length : 0;
          this.showSnackbar(`Successfully added ${successCount} video${successCount !== 1 ? 's' : ''}`);

          // Close dialog if all succeeded
          if (response.status === 'success') {
            this.dialog = false;
          }
        } else {
          throw new Error(response.message || 'Failed to add videos');
        }
      } catch (error) {
        console.error('Error adding videos:', error);
        this.videoUploadError = error.message || 'Failed to add videos';

        // Mark all uploading as error
        this.videoEntries.forEach(v => {
          if (v.status === 'uploading') {
            v.status = 'error';
            v.errorMessage = error.message;
          }
        });
      } finally {
        this.isAddingVideos = false;
      }
    },

    fixFileMimeType(file) {
      if (!file) return null;

      // Get file extension
      const extension = file.name.split('.').pop().toLowerCase();

      // Map extensions to correct MIME types
      const mimeTypes = {
        'wav': 'audio/wav',
        'mp3': 'audio/mpeg'
      };

      const correctMimeType = mimeTypes[extension];

      // If we have a correct MIME type and the current type is wrong, create a new File
      if (correctMimeType && file.type !== correctMimeType) {
        return new File([file], file.name, {
          type: correctMimeType,
          lastModified: file.lastModified
        });
      }

      return file;
    },

    renameFile(file, newName) {
      if (!file || !newName) return file;

      // Get the original extension
      const extension = file.name.split('.').pop().toLowerCase();

      // Create new filename with the track name and original extension
      const newFileName = `${newName}.${extension}`;


      // Create a new File object with the new name but same content and type
      return new File([file], newFileName, {
        type: file.type,
        lastModified: file.lastModified
      });
    },

    isDuplicateVideo(youtubeId) {
      if (!youtubeId || !this.existingVideos) return false;
      return this.existingVideos.some(
        video => video.youtubeId === youtubeId
      );
    },

    isDuplicateTrackName(trackName) {
      if (!trackName || !this.existingRecordings) return false;

      // Check if a recording with this name already exists
      return this.existingRecordings.some(
        recording => recording.name.toLowerCase() === trackName.toLowerCase()
      );
    },

    handleHighQualityFileUpload(file) {
      const correctedFile = this.fixFileMimeType(file);
      this.highQualityFile = correctedFile;
      this.setHighQualityFile(correctedFile);
    },

    handleRegularQualityFileUpload(file) {
      const correctedFile = this.fixFileMimeType(file);
      this.regularQualityFile = correctedFile;
      this.setRegularQualityFile(correctedFile);
    },

    async submitFiles() {
      if (
        !this.title ||
        !this.highQualityFile ||
        !this.regularQualityFile ||
        !this.uploadRecordingDate
      ) {
        console.error('Error submitting file: missing required fields');
        return;
      }

      // Check for duplicate track name
      if (this.isDuplicateTrackName(this.title)) {
        this.showSnackbar(`A track named "${this.title}" already exists. Please choose a different name.`);
        return;
      }

      // Rename files to match the title before uploading
      const renamedHighQuality = this.renameFile(this.highQualityFile, this.title);
      const renamedRegular = this.renameFile(this.regularQualityFile, this.title);

      // Update the store with renamed files
      this.setHighQualityFile(renamedHighQuality);
      this.setRegularQualityFile(renamedRegular);

      const fileInfo = {
        title: this.title,
        date: this.uploadRecordingDate.slice(0, 10),
      };

      await this.uploadFiles(fileInfo);
    },

    cleanTrackName(filename) {
      let cleaned = filename;

      // Remove patterns like "draft 1", "draft 2", "final 1", "final 2", etc.
      // Also handles variations with optional dashes before these patterns
      const patterns = [
        /\s*-?\s*(draft|final)\s*\d+\s*$/i,  // Matches " - draft 1", " draft 2", "- final 1" at end
        /\s*-?\s*(draft|final)\s*$/i,        // Matches " - draft", " final" at end
      ];

      patterns.forEach(pattern => {
        cleaned = cleaned.replace(pattern, '');
      });

      // Trim any trailing spaces or dashes
      cleaned = cleaned.replace(/[\s-]+$/, '').trim();

      return cleaned;
    },

    extractYearFromFile(file) {
      // Extract year from file's last modified date
      const fileDate = new Date(file.lastModified);
      const year = fileDate.getFullYear();
      const currentYear = new Date().getFullYear();

      // Validate the year is within reasonable range (2000 to current year)
      if (year >= 2000 && year <= currentYear) {
        return year;
      }

      // Fall back to current year if file date is unreasonable
      return currentYear;
    },

    validateTrack(track) {
      const errors = [];

      // Check track name length
      if (track.name.length > 250) {
        errors.push('Track name is too long (max 250 characters)');
      }

      // Check for invalid characters in track name
      if (track.name.includes('/') || track.name.includes('\\')) {
        errors.push('Track name contains invalid characters (/ or \\)');
      }

      // Check for duplicate track name
      if (this.isDuplicateTrackName(track.name)) {
        errors.push('Track name already exists in playlist');
      }

      return errors.length > 0 ? errors.join('; ') : null;
    },

    removeTrack(index) {
      this.batchTracks.splice(index, 1);

      // Clear error message if no tracks remain
      if (this.batchTracks.length === 0) {
        this.batchUploadError = "";
      }
    },

    handleBatchFileSelection(files) {
      if (!files || files.length === 0) {
        this.batchTracks = [];
        this.batchUploadError = "";
        return;
      }

      this.batchUploadError = "";
      const filePairs = {};

      // Group files by name (without extension)
      files.forEach(file => {
        const nameParts = file.name.split('.');
        const extension = nameParts.pop().toLowerCase();
        const baseName = nameParts.join('.');

        if (!filePairs[baseName]) {
          filePairs[baseName] = {};
        }

        if (extension === 'mp3') {
          filePairs[baseName].mp3 = this.fixFileMimeType(file);
        } else if (extension === 'wav') {
          filePairs[baseName].highQuality = this.fixFileMimeType(file);
        }
      });

      // Validate pairs and extract year from filename
      const validatedTracks = [];
      const missingPairs = [];

      Object.keys(filePairs).forEach(baseName => {
        const pair = filePairs[baseName];

        if (!pair.mp3 || !pair.highQuality) {
          missingPairs.push(baseName);
        } else {
          // Clean the track name (remove draft/final patterns)
          const cleanedName = this.cleanTrackName(baseName);

          // Extract year from file metadata (use mp3 file's last modified date)
          const year = this.extractYearFromFile(pair.mp3);

          const track = {
            name: cleanedName,
            mp3File: pair.mp3,
            highQualityFile: pair.highQuality,
            year: year,
            status: 'pending',
            validationError: null,
            errorMessage: null,
            progress: 0,
            statusText: '',
          };

          // Validate the track
          track.validationError = this.validateTrack(track);

          validatedTracks.push(track);
        }
      });

      if (missingPairs.length > 0) {
        this.batchUploadError = `Missing file pairs for: ${missingPairs.join(', ')}. Each track must have both a wav and mp3 file with matching names.`;
        this.batchTracks = [];
      } else {
        this.batchTracks = validatedTracks;
      }
    },

    async submitBatchUpload() {
      this.isBatchUploading = true;

      for (let i = 0; i < this.batchTracks.length; i++) {
        const track = this.batchTracks[i];

        // Skip tracks with validation errors
        if (track.validationError) {
          continue;
        }

        track.status = 'uploading';
        track.progress = 0;
        track.statusText = 'Preparing...';

        try {
          // Rename files to match the track name before uploading
          const renamedMp3 = this.renameFile(track.mp3File, track.name);
          const renamedHighQuality = this.renameFile(track.highQualityFile, track.name);

          await this.uploadBatchFile({
            name: track.name,
            year: track.year,
            mp3File: renamedMp3,
            highQualityFile: renamedHighQuality,
            onProgress: (type, value) => {
              if (type === 'status') {
                track.statusText = value;
              } else if (type === 'highQuality') {
                // HQ upload = 0-50% of total progress
                track.progress = Math.round(value / 2);
              } else if (type === 'regularQuality') {
                // MP3 upload = 50-100% of total progress
                track.progress = 50 + Math.round(value / 2);
              }
            },
          });

          track.status = 'completed';
          track.progress = 100;
        } catch (error) {
          console.error(`Failed to upload ${track.name}:`, error);
          track.status = 'error';
          track.errorMessage = error.userMessage || error.message || 'Upload failed';
          track.progress = 0;
          track.statusText = '';
        }
      }

      this.isBatchUploading = false;

      // Check if all uploads succeeded
      const uploadedTracks = this.batchTracks.filter(track => !track.validationError);
      const allSucceeded = uploadedTracks.every(track => track.status === 'completed');
      const anySucceeded = uploadedTracks.some(track => track.status === 'completed');

      // Reload the master playlist whenever anything actually landed, so the new tracks
      // show up in the list without a manual refresh. Partial success counts: the tracks
      // that did upload are on the server either way.
      if (anySucceeded) {
        await this.loadMasterPlaylist();
      }

      if (allSucceeded) {
        this.showSnackbar(`Successfully uploaded ${uploadedTracks.length} track${uploadedTracks.length > 1 ? 's' : ''}`);

        // Reset batch upload state
        this.batchTracks = [];
        this.batchUploadError = "";
        this.uploadMode = 0;
        this.dialog = false;
      } else {
        this.showSnackbar("Some uploads failed. Please check the status and try again.");
      }
    },

    showUploadSuccessMessage() {
      this.showSnackbar('Recording successfully uploaded');
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.title = '';
      this.highQualityFile = null;
      this.regularQualityFile = null;
      this.uploadRecordingDate = null;
      this.uploadRecordingFormValid = false;

      // Also reset batch upload
      this.batchTracks = [];
      this.batchUploadError = "";
      this.uploadMode = 0;

      // Reset video entries
      this.videoEntries = [this.createEmptyVideoEntry()];
      this.videoUploadError = "";

      // Reset poll create form
      this.pollTitle = '';
      this.pollOptions = [{ label: '' }, { label: '' }];
      this.pollCreateError = '';
    },

    // ── Polls tab ──
    addPollOption() {
      if (this.pollOptions.length < 3) this.pollOptions.push({ label: '' });
    },

    removePollOption(index) {
      if (this.pollOptions.length > 1) this.pollOptions.splice(index, 1);
    },

    async createPollSubmit() {
      if (!this.pollFormValid) return;
      const options = this.pollOptions.map(o => o.label.trim()).filter(Boolean);
      this.isCreatingPoll = true;
      this.pollCreateError = '';
      try {
        await this.$store.dispatch('poll/createPoll', { title: this.pollTitle.trim(), options });
        this.showSnackbar('Poll created');
        this.pollTitle = '';
        this.pollOptions = [{ label: '' }, { label: '' }];
      } catch (error) {
        this.pollCreateError = 'Could not create the poll. Please try again.';
      } finally {
        this.isCreatingPoll = false;
      }
    },

    statusColor(status) {
      if (status === 'active') return 'success';
      if (status === 'closed') return 'grey';
      return 'primary';
    },

    resultsUrl(hash) {
      // Use the current origin so the link works in any environment
      // (localhost while developing, the live domain in production).
      return window.location.origin + '/poll/' + hash;
    },

    // Every shareable view for a poll: the full results plus one per category.
    // Category links carry ?category=<label>, which the results page reads to show
    // only that column (see PollResultsTable).
    resultsLinks(poll) {
      if (!poll.share_hash) return [];
      const base = this.resultsUrl(poll.share_hash);
      const links = [{ label: 'All', url: base }];
      (poll.options || []).forEach(o => {
        links.push({ label: o.label, url: base + '?category=' + encodeURIComponent(o.label) });
      });
      return links;
    },

    async copyLink(url) {
      await this.copyToClipboard(url);
      this.showSnackbar('Link copied');
    },

    openLink(url) {
      // noopener/noreferrer so the opened tab can't reach back via window.opener.
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    async activatePoll(poll) {
      this.busyPollId = poll.id;
      try {
        await this.$store.dispatch('poll/setPollStatus', { pollId: poll.id, status: 'active' });
        this.showSnackbar('Poll activated');
      } catch (e) {
        this.showSnackbar('Could not activate the poll');
      } finally {
        this.busyPollId = null;
      }
    },

    async closePoll(poll) {
      this.busyPollId = poll.id;
      try {
        await this.$store.dispatch('poll/setPollStatus', { pollId: poll.id, status: 'closed' });
        this.showSnackbar('Poll closed');
      } catch (e) {
        this.showSnackbar('Could not close the poll');
      } finally {
        this.busyPollId = null;
      }
    },

    async generateResultsLink(poll) {
      this.linkBusyPollId = poll.id;
      try {
        let hash = poll.share_hash;
        if (!hash) {
          const res = await this.$store.dispatch('poll/createResultsLink', poll.id);
          hash = res && res.share_hash;
        }
        if (hash) {
          await this.copyToClipboard(this.resultsUrl(hash));
          this.showSnackbar('Results link copied');
        }
      } catch (e) {
        this.showSnackbar('Could not create the results link');
      } finally {
        this.linkBusyPollId = null;
      }
    },

    async copyToClipboard(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return;
        }
      } catch (e) {
        // fall through to legacy copy
      }
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    },

    askDeletePoll(poll) {
      this.pendingDeletePoll = poll;
      this.isShowingPollDeleteDialog = true;
    },

    cancelDeletePoll() {
      this.pendingDeletePoll = null;
      this.isShowingPollDeleteDialog = false;
    },

    async confirmDeletePoll() {
      if (!this.pendingDeletePoll) return;
      const poll = this.pendingDeletePoll;
      this.deleteBusyPollId = poll.id;
      try {
        await this.$store.dispatch('poll/deletePoll', poll.id);
        this.showSnackbar('Poll deleted');
      } catch (e) {
        this.showSnackbar('Could not delete the poll');
      } finally {
        this.deleteBusyPollId = null;
        this.isShowingPollDeleteDialog = false;
        this.pendingDeletePoll = null;
      }
    },
  }
};
</script>

<style>
/* Global styles to hide scrollbar in this dialog (unscoped to reach Vuetify internals) */
.highestZIndex .v-overlay__content,
.highestZIndex .v-dialog,
.highestZIndex .v-card,
.highestZIndex * {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
}

.highestZIndex .v-overlay__content::-webkit-scrollbar,
.highestZIndex .v-dialog::-webkit-scrollbar,
.highestZIndex .v-card::-webkit-scrollbar,
.highestZIndex *::-webkit-scrollbar {
  display: none !important; /* Chrome, Safari, Opera */
  width: 0 !important;
  height: 0 !important;
}
</style>

<style scoped>
.highestZIndex {
  z-index: 999999999 !important;
}

.video-entry-border {
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 8px;
}

.youtube-id-chip-col {
  display: flex;
  align-items: flex-start;
  padding-top: 4px !important;
}

.youtube-id-chip {
  margin-top: -2px;
}
</style>
